import { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from '../../firebase/firebase.js';
import { withAuth } from '../../api/withAuth';
import { logError } from '../../firebase/errorReporting';
import { useIsAdmin } from './useIsAdmin';

const api = withAuth(axios.create({ baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL }));

// The API's session lasts an hour; renew well before.
const RENEW_MS = 45 * 60 * 1000;

// One session for the whole page, however many components ask for it.
let pending = null;
let validUntil = 0;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

function establish() {
  if (Date.now() < validUntil) return Promise.resolve(true);
  if (!pending) {
    pending = api.post('/session')
      .then(() => { validUntil = Date.now() + RENEW_MS; return true; })
      .catch((err) => { logError(err, { component: 'useAdminSession' }); return false; })
      .finally(() => { pending = null; notify(); });
  }
  return pending;
}

/**
 * True once this admin has an API session cookie (gf_admin).
 *
 * The admin-only layers come through the API (/service/api/layers/*) as map
 * tiles and <img> thumbnails, which cannot carry an Authorization header; the
 * API takes the cookie for those GETs instead. POST /service/api/session swaps
 * the ID token for it (gold-finder-api routes/layers.js). The modifier
 * progress stream (an EventSource) relies on it too.
 *
 * `needed` false: does nothing and returns false - for builds that serve none
 * of these layers. Non-admins never ask (the API would refuse them anyway).
 */
export function useAdminSession(needed = true) {
  const isAdmin = useIsAdmin();
  const [ready, setReady] = useState(Date.now() < validUntil);

  useEffect(() => {
    if (!needed || !isAdmin) { setReady(false); return undefined; }
    let alive = true;
    const update = () => { if (alive) setReady(Date.now() < validUntil); };
    listeners.add(update);
    establish();
    const renew = setInterval(() => { validUntil = 0; establish(); }, RENEW_MS);
    // A different user signing in on this page needs their own session.
    const unsub = auth.onIdTokenChanged((u) => { if (u) { validUntil = 0; establish(); } });
    return () => { alive = false; listeners.delete(update); clearInterval(renew); unsub(); };
  }, [needed, isAdmin]);

  return needed && isAdmin && ready;
}
